import type { ComponentProps } from 'react';

type IconProps = ComponentProps<'svg'>;

const WORD_EXTS = new Set(['.doc', '.docx']);
const EXCEL_EXTS = new Set(['.xls', '.xlsx', '.csv']);
const PPT_EXTS = new Set(['.ppt', '.pptx']);
const IMAGE_EXTS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp']);

/** Base: white rounded card with border */
const FileBase = ({ children }: { children?: React.ReactNode }) => (
  <>
    <rect x="3" y="3" width="34" height="34" rx="5" fill="white" stroke="#E2E8F0" strokeWidth="0.8" />
    {children}
  </>
);

export const IconPdf = (props: IconProps) => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" {...props}>
    <FileBase>
      <path d="M12 14h16M12 18h14M12 22h10M12 26h16" stroke="#FECACA" strokeWidth="1.5" strokeLinecap="round" />
    </FileBase>
  </svg>
);

export const IconDoc = (props: IconProps) => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" {...props}>
    <FileBase>
      <path d="M12 14h16M12 18h14M12 22h10M12 26h16" stroke="#BFDBFE" strokeWidth="1.5" strokeLinecap="round" />
    </FileBase>
  </svg>
);

export const IconXls = (props: IconProps) => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" {...props}>
    <FileBase>
      <rect x="10" y="11" width="9" height="6" rx="1" fill="#BBF7D0" />
      <rect x="21" y="11" width="9" height="6" rx="1" fill="#DCFCE7" />
      <rect x="10" y="19" width="9" height="6" rx="1" fill="#DCFCE7" />
      <rect x="21" y="19" width="9" height="6" rx="1" fill="#BBF7D0" />
      <rect x="10" y="27" width="9" height="6" rx="1" fill="#BBF7D0" />
      <rect x="21" y="27" width="9" height="6" rx="1" fill="#DCFCE7" />
    </FileBase>
  </svg>
);

export const IconPpt = (props: IconProps) => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" {...props}>
    <FileBase>
      <rect x="10" y="12" width="20" height="14" rx="2" fill="#FED7AA" opacity="0.6" />
      <rect x="12" y="14" width="16" height="10" rx="1.5" fill="#FDBA74" opacity="0.4" />
    </FileBase>
  </svg>
);

export const IconImage = (props: IconProps) => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" {...props}>
    <FileBase>
      <circle cx="16" cy="16" r="3" fill="#DDD6FE" />
      <path d="M10 28l6-6 4 4 4-3 6 5" stroke="#C4B5FD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </FileBase>
  </svg>
);

export const IconFile = (props: IconProps) => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" {...props}>
    <FileBase>
      <path d="M12 14h16M12 18h14M12 22h10M12 26h16" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" />
    </FileBase>
  </svg>
);

/** Render file type icon by extension */
export const FileTypeIcon = ({ ext, size = 40 }: { ext: string; size?: number }) => {
  const props = { width: size, height: size };
  if (ext === '.pdf') return <IconPdf {...props} />;
  if (WORD_EXTS.has(ext)) return <IconDoc {...props} />;
  if (EXCEL_EXTS.has(ext)) return <IconXls {...props} />;
  if (PPT_EXTS.has(ext)) return <IconPpt {...props} />;
  if (IMAGE_EXTS.has(ext)) return <IconImage {...props} />;
  return <IconFile {...props} />;
};

/** Get file type chip style key (matches FileCard.module.scss class names) */
export const getFileTypeChipKey = (ext: string): string => {
  if (ext === '.pdf') return 'chipPdf';
  if (WORD_EXTS.has(ext)) return 'chipDoc';
  if (EXCEL_EXTS.has(ext)) return 'chipXls';
  if (PPT_EXTS.has(ext)) return 'chipPpt';
  return 'chipDefault';
};
